import {createApp} from "vue";
import App from "./App.vue";
import {createRouter, createWebHashHistory} from 'vue-router'
import vuetify from "./plugins/vuetify";
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';


import 'primevue/resources/themes/saga-blue/theme.css';
import 'primevue/resources/primevue.min.css';
import 'primeicons/primeicons.css';
import "primeflex/primeflex.css";
import './assets/reset.scss';


import {loadFonts} from './plugins/webfontloader'
// Signin stays eager (first screen); everything else is lazy-loaded per route
// so the entry chunk doesn't carry every view. Meetings is prefetched because
// it's the post-login redirect target.
import Signin from "./views/SigninScreen.vue";
const Inputs = () => import("./views/Inputs/InputsScreen.vue");
const Meetings = () => import(/* webpackPrefetch: true */ "./views/Meetings/MeetingOverview.vue");
const Registrations = () => import("./views/Registrations/RegistrationsScreen.vue");
const Comment = () => import("./views/CommentEdit/CommentEdit.vue");
const CommentInsert = () => import("./views/CommentEdit/InsertEdit.vue");
const HarnessSilks = () => import("./views/HarnessSilks/HarnessSilks.vue");
const AIComment = () => import("./views/CommentEdit/AIComment.vue");
const AIRacePreview = () => import("./views/CommentEdit/AIRacePreview.vue");
const Database = () => import("./views/Database/DatabaseMain.vue");
const Admin = () => import("./views/Admin/AdminMain.vue");
import {defaultConfig, plugin} from '@formkit/vue'
import '@formkit/themes/genesis'
import ConfirmationService from 'primevue/confirmationservice';
import "@aws-amplify/ui-components";
import {applyPolyfills, defineCustomElements,} from "@aws-amplify/ui-components/loader";
import Amplify from "aws-amplify";
import AmplifyVue from '@aws-amplify/ui-vue';
import '@aws-amplify/ui-vue/styles.css';

import {Hub} from "@aws-amplify/core"
import Auth from "@aws-amplify/auth"
import {API} from "aws-amplify";

import store from './store/index.js'

import awsconfig from "./aws-exports.js";


let user;

getUser().then((user) => {
    if (user) {
        router.push({path: '/'});
    }
});

function getUser() {
    return Auth.currentAuthenticatedUser().then((data) => {
        if (data && data.signInUserSession) {
            store.commit('setUser', data);
            return data;
        }
    }).catch(() => {
        store.commit('setUser', null);
        return null;
    });
}


function getSilksAdminUrl(){
    const loc = window.location.hostname === "www.admin.medialityracing.com.au" ? "silksadmin" : "silksadmin.stg";
    return `https://${loc}.medialityracing.com.au`;
}

Hub.listen("auth", async (data) => {
    if (data.payload.event === 'signOut') {
        user = null;
        store.commit('setUser', null);
        router.push({path: '/'});
    } else if (data.payload.event === 'signIn') {
        user = await getUser();
        router.push({path: '/meetings'});
    }
});

const routes = [
    {
        path: "/",
        name: "Signin",
        component: Signin,
    },
    {
        path: "/meetings",
        name: "Meetings",
        component: Meetings,
        meta: {requiresAuth: true}
    },
    {
        path: "/db",
        name: "Database",
        component: Database,
        meta: {requiresAuth: true}
    },
    {
        path: "/registrations",
        name: "Registrations",
        component: Registrations,
        meta: {requiresAuth: true}
    },
    {
        path: "/admin",
        name: "Admin",
        component: Admin,
        meta: {requiresAuth: true}
    },
    {
        path: "/comment",
        name: "Comment",
        component: Comment,
        meta: {requiresAuth: true}
    },
    {
        path: "/commentinsert",
        name: "Comment Insert",
        component: CommentInsert,
        meta: {requiresAuth: true}
    },
    {
        path: "/aicomment",
        name: "AI Comment",
        component: AIComment,
        meta: {requiresAuth: true}
    },
    {
        path: "/inputs",
        name: "Inputs",
        component: Inputs,
        meta: {requiresAuth: true}
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        // component: function () {
        //   return import(/* webpackChunkName: "about" */ "../views/Inputs.vue");
        // },
    },
    {
        path: "/harnesssilks",
        name: "Harness Silks",
        component: HarnessSilks,
        meta: {requiresAuth: true}
    },
    {
        path: '/silksadmin',
        beforeEnter() {location.href =  getSilksAdminUrl()}
    },
    {
        path: "/airacepreview",
        name: "AI Race Preview",
        component: AIRacePreview,
        meta: {requiresAuth: true}
    },

];
const router = createRouter({
    // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
    history: createWebHashHistory(),
    routes, // short for `routes: routes`
})

// A deploy replaces hashed chunk files; a stale tab then fails to load a lazy
// route chunk. Reload once to pick up the new build. Only once per route: if
// index.html is itself served from cache it keeps naming the deleted chunks,
// and reloading again would loop forever.
const CHUNK_RELOAD_KEY = "chunkReloadedFor";

router.onError((error, to) => {
    if (!/ChunkLoadError|Loading chunk .* failed/i.test(String(error))) return;

    const path = to?.fullPath || window.location.hash;
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === path) {
        alert(
            "Failed to load part of the app. Please hard-refresh the page " +
            "(Cmd+Shift+R on Mac, Ctrl+Shift+R on Windows)."
        );
        return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, path);
    if (to?.fullPath) window.location.hash = to.fullPath;
    window.location.reload();
});

// A navigation that resolved means chunks are loading again — drop the guard so
// a later deploy in the same tab still gets its one reload.
router.afterEach(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY));

router.beforeResolve(async (to, from, next) => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
        user = await getUser();
        if (!user) {
            return next({
                path: '/'
            });
        }
        return next()
    }
    return next()
});

loadFonts()

Amplify.configure(awsconfig);
API.configure(awsconfig);
Auth.configure(awsconfig);

createApp(App)
    .use(AmplifyVue)
    .use(PrimeVue)
    .use(ConfirmationService)
    .use(ToastService)
    .use(plugin, defaultConfig)
    .use(vuetify)
    .use(store)
    .use(router)
    .mount('#app')


applyPolyfills().then(() => {
    defineCustomElements(window);
});
